import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { formatAccountNumber } from '@eika-infrastruktur/format';
import { FocusContainer } from '@eika/focus-container';
import { WarningLabel } from '@eika/label';

import type { Account, AccountNumber } from '../../../../generated-types';
import useSelect from '../../utils/useSelect';
import InvalidAccountsList from './InvalidAccountsList';
import SelectedAccountButton from './SelectedAccountButton';
import ValidAccountsList from './ValidAccountsList';

import './FromAccountSelect.scss';

type FromAccountSelectProps = {
    className?: string;
    id: string;
    label?: string;
    placeholder: string;
    items: Account[];
    invalidItems?: Account[];
    onSelect: (accountNumber: AccountNumber) => void;
    selected: AccountNumber | undefined;
    selectedAccount: Account | undefined;
    locked: boolean;
    isLoading?: boolean;
    error?: { className: string; message: string };
    isPaymentModalOpen?: boolean;
    bottomLabel?: React.ReactNode;
    tabIndex?: number;
    setPlaceholderHeight?: (height: number) => void;
    showNegativeAmount?: boolean;
    focusFirst?: boolean;
    describedBy?: string;
};

const FromAccountSelect: React.FC<FromAccountSelectProps> = ({
    className,
    id,
    label,
    placeholder,
    items,
    invalidItems,
    onSelect,
    selected,
    locked,
    isLoading = false,
    error,
    isPaymentModalOpen = false,
    bottomLabel,
    tabIndex,
    setPlaceholderHeight,
    showNegativeAmount = false,
    focusFirst = false,
    selectedAccount,
    describedBy,
}) => {
    const selectBoxRef = useRef<HTMLLabelElement | null>(null);
    const selectListRef = useRef<HTMLUListElement | null>(null);
    const selectRef = useRef<HTMLButtonElement | null>(null);
    const {
        onKeyDownSelect,
        onKeyDownSelectItem,
        closeSelect,
        show: open,
        setShow: setOpen,
    } = useSelect(selectListRef, selectBoxRef as React.RefObject<HTMLDivElement>, selectRef);

    useEffect(() => {
        if (!isPaymentModalOpen) {
            setOpen(false);
        }
    }, [setOpen, isPaymentModalOpen]);

    const selectItem = useCallback(
        (accountNumber: AccountNumber) => {
            onSelect(accountNumber);
        },
        [onSelect],
    );

    const listItemKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLButtonElement>, account: AccountNumber) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                selectItem(account);
            }
            if (e.key === 'Esc' || e.key === 'Escape') {
                if (open) {
                    e.stopPropagation();
                }
                closeSelect();
                return;
            }

            onKeyDownSelectItem(e);
        },
        [open, selectItem, onKeyDownSelectItem, closeSelect],
    );

    const selectKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLButtonElement>) => {
            if (e.key === 'Esc' || e.key === 'Escape') {
                if (open) {
                    e.stopPropagation();
                }
                closeSelect();
                return;
            }
            onKeyDownSelect(e);
        },
        [onKeyDownSelect, open, closeSelect],
    );
    const formattedAccountNumber = formatAccountNumber(selected);

    const itemsWithoutSelected = selected ? items.filter((x) => x.accountNumber.value !== selected.value) : items;

    const placeholderHeight = useMemo(() => Math.min(150, (Math.max(items.length, 1) * 100) / 1.5), [items]);

    const currentAccountIsBlocked = !!invalidItems?.find((x) => x.accountNumber.value === selected?.value);

    useEffect(() => {
        setPlaceholderHeight?.(open ? placeholderHeight : 0);
    }, [open, setPlaceholderHeight, placeholderHeight]);

    return (
        <>
            <label
                aria-describedby={describedBy}
                htmlFor={id}
                className={classNames('from-account-select', className, { active: open, locked, 'no-label': !label })}
                ref={selectBoxRef}
                data-testid="from-account-select"
            >
                <FocusContainer
                    entry={open}
                    onExit={() => setOpen(false)}
                    containerClassName="from-account-select__focus-container"
                >
                    <SelectedAccountButton
                        id={id}
                        placeholder={placeholder}
                        showNegativeAmount={showNegativeAmount}
                        label={label}
                        open={open}
                        focusFirst={focusFirst}
                        selectRef={selectRef}
                        locked={locked}
                        formattedAccountNumber={formattedAccountNumber}
                        setOpen={setOpen}
                        selectKeyDown={selectKeyDown}
                        selected={selected}
                        tabIndex={tabIndex}
                        selectedAccount={selectedAccount}
                        isBlocked={currentAccountIsBlocked}
                    />

                    <div className={classNames('from-account-select__dropdown-wrapper', { active: open })}>
                        <ValidAccountsList
                            selected={selected}
                            id={id}
                            selectListRef={selectListRef}
                            isLoading={isLoading}
                            itemsWithoutSelected={itemsWithoutSelected}
                            selectItem={selectItem}
                            listItemKeyDown={listItemKeyDown}
                            showNegativeAmount={showNegativeAmount}
                            items={items}
                        />

                        {!isLoading && invalidItems && (
                            <InvalidAccountsList
                                id={id}
                                showNegativeAmount={showNegativeAmount}
                                invalidItems={invalidItems}
                                setOpen={setOpen}
                                open={open}
                            />
                        )}
                    </div>
                </FocusContainer>
            </label>
            {!error && bottomLabel}
            {error && <WarningLabel className={error.className} text={error.message} />}
        </>
    );
};

export default FromAccountSelect;
